import React from 'react'
import type { ComponentProps, PartData } from '@enonic/react-components'
import RelatedArticlesPrototype from '/react4xp/components/prototype/RelatedArticles'
import type { RelatedArticleCard as PrototypeCard, RelatedArticlesProps } from '/react4xp/components/prototype/RelatedArticles/types'
import { EditorPlaceholder } from '/react4xp/components/shared/EditorPlaceholder'

// Raw processor shape (data.*) - distinct from the prototype's own card/props types imported above.
type RelatedArticleCard = {
	href: string | null
	title: string
	image?: { src: string; alt?: string }
	type: string
}

type RelatedArticlesPartData = {
	overline?: string
	heading?: string
	articles?: RelatedArticleCard[]
	viewAllLink?: { label: string; url: string; isExternal: boolean }
}

// Card is an anchor in the prototype, so omit articles without an href (its url is non-nullable).
function toCard(article: RelatedArticleCard): PrototypeCard | null {
	if (!article.href) return null

	return {
		image: article.image?.src ? { src: article.image.src, alt: article.image.alt } : undefined,
		label: article.type,
		title: article.title,
		url: article.href,
		isExternal: false, // article links come from myPageUrl, which never returns an external URL
	}
}

export const RelatedArticles = ({ meta, data }: ComponentProps<PartData>) => {
	const { overline, heading, articles = [], viewAllLink } = (data || {}) as RelatedArticlesPartData

	const cards = articles.map(toCard).filter((card): card is PrototypeCard => card !== null)

	// An unconfigured part (or a site with too few articles) still has to render
	// something in Content Studio, or the editor cannot select it to fill it in -
	// but it must not render outside edit mode (US-004). Guarding on the mapped cards
	// keeps href-less articles from leaving an empty section behind.
	if (cards.length === 0) {
		return (
			<EditorPlaceholder
				mode={meta?.mode}
				componentClass="related-articles"
				title="Related articles"
				heading={[overline, heading].filter(Boolean).join(' - ')}
				reason={
					articles.length > 0
						? `${articles.length} article(s) found, but none has a page URL.`
						: 'No related articles found.'
				}
			/>
		)
	}

	const mappedViewAllLink: RelatedArticlesProps['viewAllLink'] = viewAllLink && {
		text: viewAllLink.label,
		url: viewAllLink.url,
		isExternal: viewAllLink.isExternal,
	}

	return <RelatedArticlesPrototype overline={overline} heading={heading} viewAllLink={mappedViewAllLink} cards={cards} />
}
